import Joi from "joi";
import * as _ from "lodash";
import React, { useRef } from "react";
import { FieldValues, useFormState, useWatch, Control } from "react-hook-form";
import { toast } from "react-toastify";
import traverse from "traverse";
import { useDebouncedCallback } from "use-debounce";

import fetchSingleton from "@/lib/fetchSingleton";
import { humanizeError } from "@/lib/humanizeError";

type RHFormErrors = Record<string, { type?: string; message: string }>;
type RHFormErrorValue = RHFormErrors[string];

export const joiErrorToRHFormError = (
  joiError: Joi.ValidationError | undefined,
  type?: string,
): RHFormErrors => {
  const errors: RHFormErrors = {};
  joiError?.details?.forEach(it => {
    const path = it.path.join(".");
    errors[path] = { type, message: it.message };
  });
  return errors;
};

type useFormValidationOptions<T extends FieldValues> = {
  control: Control<T>;
  joiSchema: Joi.ObjectSchema<T> | Joi.AnySchema;
  context?: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  setError: Function;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  clearErrors: Function;
};

export type submitFunction<T> = (
  values: T,
  handleApiResult: (apiResult: any) => void,
) => Promise<void>;

export type submitRunner<T> = (submitFunction: submitFunction<T>) => Promise<void>;

export const INTERRUPTED = Symbol("INTERRUPTED");

export const useFormValidation = <T extends FieldValues>(
  options: useFormValidationOptions<T>,
) => {
  const { control, joiSchema, setError, context, clearErrors } = options;

  const values = useWatch({ control });
  const valuesRef = React.useRef<unknown>(undefined);
  valuesRef.current = values;
  const { touchedFields, isSubmitted } = useFormState({ control });

  const plainTouchedFieldsKeys = React.useMemo(
    () =>
      traverse(touchedFields)
        .paths()
        ?.map(it => it.join(".")),
    [JSON.stringify(touchedFields)],
  );

  const [backendErrors, setBackendErrors] = React.useState<RHFormErrors | undefined>();
  const shouldApplyErrorsImmidiately = React.useRef<boolean>(false);

  const getValidatedValues = React.useCallback(() => {
    const { value } = joiSchema.validate(values, {
      context,
      abortEarly: false,
    });
    return value;
  }, [joiSchema, JSON.stringify(context), values]);

  const changedKeys = useObjectChangedKeys(values);

  /**
   * LOGIC for set error after few socond pause in input, before user blurred field
   */
  const lastChangedKeysRef = React.useRef<string[] | undefined>(undefined);
  lastChangedKeysRef.current = changedKeys;

  /**
   * End LOGIC
   */

  React.useEffect(() => {
    setBackendErrors(_.omit(backendErrors, changedKeys));
  }, [JSON.stringify(values), changedKeys]);

  const mergedErrors = backendErrors;

  const [isErrorsOnAllFieldsAppliedOnce, setIsErrorsOnAllFieldsAppliedOnce] =
    React.useState<boolean>(false);

  const applyErrorsNow = React.useCallback(() => {
    clearErrors();
    _.mapValues(mergedErrors, (v, k) => {
      // Apply errors to all fields after for submitted once
      if (isSubmitted) return setError(k, v);
      // Apply errors to all fields applyToAllFields function called once
      if (isErrorsOnAllFieldsAppliedOnce) return setError(k, v);
      // Apply only on touched fields
      if (plainTouchedFieldsKeys?.includes(k)) return setError(k, v);
    });
    shouldApplyErrorsImmidiately.current = false;
  }, [
    clearErrors,
    mergedErrors,
    isSubmitted,
    isErrorsOnAllFieldsAppliedOnce,
    plainTouchedFieldsKeys,
    setError,
    shouldApplyErrorsImmidiately,
  ]);

  const debounceForApplyErrors = useDebouncedCallback(applyErrorsNow, 500);

  React.useEffect(() => {
    debounceForApplyErrors();
    if (shouldApplyErrorsImmidiately.current) debounceForApplyErrors.flush();
  }, [
    mergedErrors,
    applyErrorsNow,
    debounceForApplyErrors,
    shouldApplyErrorsImmidiately,
  ]);

  /**
   * Validate immediately on blur some field
   */
  React.useEffect(() => {
    const handler = () => {
      shouldApplyErrorsImmidiately.current = true;
      debounceForApplyErrors.flush();
    };
    document.addEventListener("blur", handler, true); // capture phase
    return () => document.removeEventListener("blur", handler, true);
  }, [
    // validateOnFrontend,
    shouldApplyErrorsImmidiately,
    debounceForApplyErrors,
  ]);

  const applyErrorsOnAllFields = React.useCallback(() => {
    clearErrors();
    _.mapValues(mergedErrors, (v, k) => setError(k, v));
    setIsErrorsOnAllFieldsAppliedOnce(true);
  }, [mergedErrors, setError]);

  const setBackendErrorsExtFn = React.useCallback(
    (backendErrors: RHFormErrors) => {
      shouldApplyErrorsImmidiately.current = true;
      setBackendErrors(backendErrors);
    },
    [setBackendErrors],
  );

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const hasErrors = !_.isEmpty(mergedErrors);

  const submitRunner = React.useCallback<submitRunner<T>>(
    async submitFunction => {
      try {
        if (hasErrors && !process.env.NEXT_PUBLIC_SHOULD_VALIDATE_ONLY_ON_BACKEND) {
          await new Promise(r => setTimeout(r, 1000));
          applyErrorsOnAllFields();
          return;
        }

        const handleApiResult = (apiResult: any) => {
          const { isValid, fieldErrors, globalErrors } =
            extractErrorsFromPlainBackendAnswer(apiResult);
          setBackendErrors(fieldErrors);
          applyErrorsOnAllFields();
          globalErrors.forEach(it => toast.error(it.message));
          if (!isValid) throw INTERRUPTED;
        };

        await submitFunction(getValidatedValues(), handleApiResult);
      } catch (e) {
        if (INTERRUPTED) return; // Do nothing
        console.error(e);
        toast.error(humanizeError(e));
      }
    },
    [applyErrorsOnAllFields, setBackendErrors, hasErrors, getValidatedValues],
  );

  const handleSubmit = React.useCallback(
    (submitFunction: submitFunction<T>) =>
      async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        setIsSubmitting(true);
        await submitRunner(submitFunction);
        setIsSubmitting(false);
      },
    [getValidatedValues, hasErrors, applyErrorsOnAllFields, setBackendErrors],
  );

  return React.useMemo(
    () => ({
      setBackendErrors: setBackendErrorsExtFn,
      applyErrorsNow,
      applyErrorsOnAllFields,
      hasErrors,
      getValidatedValues,
      handleSubmit,
      isSubmitting,
      submitRunner,
    }),
    [
      setBackendErrors,
      applyErrorsOnAllFields,
      mergedErrors,
      isSubmitting,
      handleSubmit,
      submitRunner,
      hasErrors,
    ],
  );
};

export function useObjectChangedKeys<T extends Record<string, any>>(obj: T) {
  const prevValueRef = useRef<T>(obj);
  const prevValue = prevValueRef.current;

  return React.useMemo(() => {
    const prev = traverse(prevValue)
      .paths()
      .map(it => it.join("."))
      .filter(it => !!it);
    const curr = traverse(obj)
      .paths()
      .map(it => it.join("."))
      .filter(it => !!it);

    const intersectionOfPaths = _.intersection(prev, curr);
    const newPaths = _.difference(curr, prev);

    const newChangedKeys = [...intersectionOfPaths, ...newPaths]
      .map(it => {
        const prevVal = _.get(prevValue, it);
        const currVal = _.get(obj, it);
        if (!looseEqual(prevVal, currVal)) return it;
      })
      .filter((x): x is string => !!x)
      .sort((a, b) => a.localeCompare(b));

    prevValueRef.current = structuredClone(obj);

    return newChangedKeys;
  }, [JSON.stringify(obj)]);
}

export const extractErrorsFromPlainBackendAnswer = (
  apiResult: Awaited<ReturnType<typeof fetchSingleton>>,
) => {
  const ret: {
    isValid: boolean;
    fieldErrors: RHFormErrors;
    globalErrors: RHFormErrorValue[];
  } = {
    isValid: false,
    fieldErrors: {},
    globalErrors: [],
  };
  try {
    if (
      apiResult.status === "ERROR" &&
      apiResult.subStatus === "VALIDATION_ERROR" &&
      apiResult.errors?.some(
        (it: unknown) =>
          typeof it === "object" &&
          it !== null &&
          "type" in it &&
          it.type === "input_error" &&
          "jsonPath" in it &&
          "message" in it,
      )
    ) {
      apiResult.errors.map((it: unknown) => {
        if (
          typeof it === "object" &&
          it !== null &&
          "type" in it &&
          it.type === "input_error" &&
          "jsonPath" in it &&
          "message" in it &&
          it.jsonPath &&
          typeof it.jsonPath === "string"
        ) {
          ret.fieldErrors[it.jsonPath] = {
            type: "server",
            message: (() => {
              if (it.message) return it.message as string;
              return "Invalid field, unknown error";
            })(),
          };
        }
      });
    } else if (
      apiResult.status === "ERROR" &&
      apiResult.subStatus === "VALIDATION_ERROR" &&
      apiResult.errors?.some(
        (it: unknown) =>
          typeof it === "object" &&
          it !== null &&
          "status" in it &&
          it.status === "not_found" &&
          "message" in it,
      )
    ) {
      ret.globalErrors.push({
        type: "server",
        message: humanizeError(
          apiResult.errors.find(
            (it: unknown) =>
              typeof it === "object" &&
              it !== null &&
              "status" in it &&
              it.status === "not_found" &&
              "message" in it,
          )?.message,
        ) as string,
      });
    } else if (
      apiResult.status === "ERROR" &&
      apiResult.subStatus === "VALIDATION_ERROR" &&
      apiResult.errors?.some(
        (it: unknown) =>
          typeof it === "object" &&
          it !== null &&
          "location" in it &&
          "type" in it &&
          "message" in it,
      )
    ) {
      apiResult.errors.map((error: unknown) => {
        if (
          typeof error === "object" &&
          error !== null &&
          "location" in error &&
          "type" in error &&
          "message" in error &&
          error.location &&
          typeof error.location === "string"
        ) {
          ret.fieldErrors[error.location] = {
            type: "server",
            message: (() => {
              if (
                error.type === "string_pattern_mismatch" &&
                error.location === "postal_code"
              )
                return "Invalid postal code format.";
              if (error.type === "string_pattern_mismatch") return "Invalid format";
              if (error.message) return error.message as string;
              return "Invalid";
            })(),
          };
        }
      });
    } else if (
      apiResult.status === "ERROR" &&
      "errors" in apiResult &&
      apiResult.errors.some(
        (it: unknown) =>
          typeof it === "object" && it !== null && "message" in it && it.message,
      )
    ) {
      apiResult.errors.forEach((it: unknown) => {
        if (
          typeof it === "object" &&
          it !== null &&
          "message" in it &&
          typeof it.message === "string"
        ) {
          ret.globalErrors.push({
            type: "server",
            message: humanizeError(it.message) as string,
          });
        }
      });
    } else if (
      apiResult.status === "ERROR" ||
      (apiResult.status === "OK" &&
        apiResult.list?.some(
          (it: unknown) =>
            typeof it === "object" &&
            it !== null &&
            "status" in it &&
            "message" in it &&
            it.status === "not_found",
        ))
    ) {
      ret.globalErrors.push({
        type: "server",
        message: humanizeError(apiResult, "Validate") as string,
      });
    } else if (apiResult.status === "OK") {
      ret.isValid = true;
      // TODO Maybe Do something here
      // return NO_ERRORS_FOUND;
    }
  } catch (e) {
    console.error(e);
    ret.globalErrors.push({
      type: "server",
      message: humanizeError(e, "Validation Failed") as string,
    });
  }
  return ret;
};

function normalizeFalsyValues(x: unknown) {
  return x == null || x === "" || x === false ? undefined : x;
}

function looseEqual(a: unknown, b: unknown) {
  if (_.isArray(a) && _.isArray(b)) return _.isEqual(a, b);
  if (_.isPlainObject(a) && _.isPlainObject(b)) return _.isEqual(a, b);
  return normalizeFalsyValues(a) == normalizeFalsyValues(b);
}
